import { useEffect, useRef } from "react";
import { Section } from "@/components/sections/Section";
import type { Section as SectionType } from "@/types/global";

type GravityFormSection = Extract<SectionType, { type: "gravity_form" }>;

export function GravityForm({ settings, data }: GravityFormSection) {
  const { title, form_id: formId, html } = data;
  const formContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = formContainerRef.current;

    if (!root) {
      return;
    }

    // Re-execute inline <script> tags injected via dangerouslySetInnerHTML.
    // Browsers won't run scripts created through innerHTML, so Gravity Forms'
    // per-form init (AJAX iframe handler, spinner, validation) never fires.
    root.querySelectorAll("script").forEach((oldScript) => {
      const newScript = document.createElement("script");
      for (const attr of Array.from(oldScript.attributes)) {
        newScript.setAttribute(attr.name, attr.value);
      }
      newScript.text = oldScript.textContent ?? "";
      oldScript.parentNode?.replaceChild(newScript, oldScript);
    });

    let isSubmitting = false;

    const getSubmitButtons = () =>
      root.querySelectorAll<HTMLInputElement | HTMLButtonElement>(
        '.gform_footer input[type="submit"], .gform_footer button[type="submit"]'
      );

    const ensureStatus = () => {
      let status = root.querySelector<HTMLElement>(".gravity-form__status");

      if (!status) {
        status = document.createElement("p");
        status.className = "gravity-form__status";
        status.setAttribute("role", "status");
        status.setAttribute("aria-live", "polite");

        const footer = root.querySelector(".gform_footer");
        (footer ?? root).append(status);
      }

      return status;
    };

    const setSubmitting = () => {
      isSubmitting = true;
      root.classList.add("is-submitting");

      const status = ensureStatus();
      status.hidden = false;
      status.textContent = "Envoi en cours...";

      getSubmitButtons().forEach((button) => {
        button.setAttribute("aria-busy", "true");
      });
    };

    const clearSubmitting = () => {
      isSubmitting = false;
      root.classList.remove("is-submitting");

      const status = root.querySelector<HTMLElement>(".gravity-form__status");

      if (status) {
        status.remove();
      }

      getSubmitButtons().forEach((button) => {
        button.removeAttribute("aria-busy");
      });
    };

    const handleSubmit = (event: SubmitEvent) => {
      if (event.target instanceof HTMLFormElement && root.contains(event.target)) {
        setSubmitting();
      }
    };

    const observer = new MutationObserver(() => {
      if (!isSubmitting) {
        return;
      }

      const hasResponse = root.querySelector(
        ".gform_confirmation_message, .gform_validation_errors, .validation_message"
      );

      if (hasResponse) {
        clearSubmitting();
      }
    });

    root.addEventListener("submit", handleSubmit, true);
    observer.observe(root, { childList: true, subtree: true });

    return () => {
      root.removeEventListener("submit", handleSubmit, true);
      observer.disconnect();
    };
  }, [formId, html]);

  if (!html) {
    return null;
  }

  return (
    <Section settings={settings}>
      {title && (
        <div className="mb-10 max-w-3xl">
          <h2 className="heading-jumbo-small">{title}</h2>
          <span className="separateur" />
        </div>
      )}
      <div
        ref={formContainerRef}
        className="gravity-form"
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </Section>
  );
}
